import "./PhoneInputField.scss";

import clsx from "clsx";
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  defaultCountries,
  guessCountryByPartialPhoneNumber,
  PhoneInput,
  removeDialCode,
} from "react-international-phone";

import { ErrorMessage } from "@/components/Interface/FormFields/ErrorMessage/ErrorMessage";
import { InputLabel } from "@/components/Interface/FormFields/InputLabel/InputLabel";
import { getNestedValue } from "@/utils/objectUtils";

type PhoneInputFieldProps<TFormValues extends FieldValues> = {
  id: string;
  label: string;
  name: Path<TFormValues>;
  control: Control<TFormValues>;
  errors: FieldErrors;
  disabled?: boolean;
  readOnly?: boolean;
  required?: string | boolean;
  validationMessage?: string;
  backendError?: string;
};

export const PhoneInputField = <TFormValues extends FieldValues>({
  id,
  label,
  name,
  control,
  errors,
  disabled = false,
  readOnly = false,
  required,
  validationMessage,
  backendError,
}: PhoneInputFieldProps<TFormValues>) => {
  const { t } = useTranslation();

  const fieldError = getNestedValue(errors, name);
  const hasError = fieldError !== undefined;

  const rawMessage = fieldError?.message as string | undefined;
  const errorMessage =
    rawMessage ||
    (fieldError?.type === "backendError" ? backendError : undefined) ||
    "";

  const isOptional = !required && !disabled && !readOnly;
  const optionalLabel = t("translation:Common.label.optional");
  const labelWithOptional = isOptional ? `${label} ${optionalLabel}` : label;

  return (
    <div className="form-field-container phone-input-container">
      <div className="label-container">
        <InputLabel
          error={hasError}
          htmlFor={id}
          label={labelWithOptional}
          disabled={disabled || readOnly}
        />
      </div>
      <Controller
        name={name}
        control={control}
        rules={{
          validate: (value: unknown) => {
            const phone = (value as string) || "";
            const stripped = phone.replace(/[\s\-()/]/g, "");

            if (!stripped) {
              return typeof required === "string"
                ? required
                : String(required || "");
            }

            if (!/^\+[1-9]\d+$/.test(stripped) || stripped.length < 7) {
              return validationMessage || String(required || "");
            }

            return true;
          },
        }}
        render={({ field }) => {
          const handleChange = (
            newPhone: string,
            meta: { country: { dialCode: string } },
          ) => {
            const newDialCode = "+" + meta.country.dialCode;
            const prevPhone = (field.value as string) || "";

            // When only the country is switched the library emits just the new
            // dial code (e.g. "+49"). Detect this and reattach the national
            // number by asking the library to identify the previous country.
            if (
              newPhone === newDialCode &&
              prevPhone &&
              prevPhone !== newDialCode
            ) {
              const { country: prevCountry } = guessCountryByPartialPhoneNumber(
                {
                  phone: prevPhone,
                  countries: defaultCountries,
                },
              );

              if (prevCountry) {
                const nationalNumber = removeDialCode({
                  phone: prevPhone,
                  dialCode: prevCountry.dialCode,
                });

                if (nationalNumber) {
                  field.onChange(newDialCode + nationalNumber);

                  return;
                }
              }
            }

            field.onChange(newPhone);
          };

          return (
            <PhoneInput
              className={clsx(
                hasError && "error",
                (disabled || readOnly) && "disabled",
              )}
              inputProps={{
                id,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error data-testid is a valid HTML attribute
                "data-testid": String(name),
                "aria-required": !!required,
                "aria-invalid": hasError ? "true" : "false",
              }}
              value={(field.value as string) ?? ""}
              defaultCountry="ch"
              onBlur={field.onBlur}
              disabled={disabled || readOnly}
              onChange={handleChange}
            />
          );
        }}
      />
      {errorMessage && (
        <ErrorMessage message={errorMessage} name={String(name)} />
      )}
    </div>
  );
};
